<template>
  <bm-control v-bind="$attrs" :read-only-value="formattedReadOnlyValue">
    <template #default="{ dataQa }">
      <v-text-field
        class="bm-text"
        :persistent-hint="true"
        :placeholder="placeholderVal"
        :value="value"
        :prefix="prefix"
        :suffix="truncatedSuffix"
        v-bind="$attrs"
        color="brand02"
        :label="undefined"
        :data-qa="dataQa"
        :data-component="dataComponent"
        @input="emit('update:value', $event ?? '')"
        @focus="emit('focus', $event)"
        @blur="blurEvent($event)"
        @keydown="emit('keydown', $event)"
        @mousedown="emit('mousedown', $event)"
        @touchstart="emit('touchstart', $event)"
        @click:append="emit('click:append', $event)"
      >
        <template #append-outer>
          <slot name="append-outer" />
        </template>
        <template #append>
          <slot name="append" />
        </template>
        <template #prepend-inner>
          <slot name="prepend-inner" />
        </template>
        <template #prepend>
          <slot name="prepend" />
        </template>
      </v-text-field>
    </template>
    <!-- Forward BmControl slots used in read-only mode -->
    <template #readOnly="scope">
      <slot name="readOnly" v-bind="scope" />
    </template>
    <template #readOnlyValue="scope">
      <slot name="readOnlyValue" v-bind="scope" />
    </template>
    <template #prepend-inner="scope">
      <slot name="prepend-inner" v-bind="scope" />
    </template>
  </bm-control>
</template>

<script setup lang="ts">
import BmControl from "@/components/controls/input/BmControl.vue";
import { computed } from "vue";
import dataComponentAttrs from "@/components/controls/DataComponentAttrs";
import truncate from "lodash-es/truncate";

interface Props {
  autoTrim?: boolean;
  value?: string;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  readOnlyValue?: string;
  maxSuffixLength?: number;
}

interface Events {
  (event: "update:value", value: string): void;
  (event: "focus", e: Event): void;
  (event: "blur", e: Event): void;
  (event: "keydown", e: KeyboardEvent): void;
  (event: "mousedown", e: Event): void;
  (event: "touchstart", e: Event): void;
  (event: "click:append", e: Event): void;
}

const props = withDefaults(defineProps<Props>(), {
  autoTrim: true,
});

const emit = defineEmits<Events>();

const dataComponent = dataComponentAttrs.bmText;

const formattedReadOnlyValue = computed(() => {
  const value = props.readOnlyValue ? props.readOnlyValue : props.value;

  if (value) {
    return (
      (props.prefix ? props.prefix + " " : "") +
      value +
      (props.suffix ? " " + props.suffix : "")
    );
  } else {
    return value;
  }
});

// This is ugly, but it makes all v-text-field controls think they have a placeholder.
// Which in turn causes the label to render above the field.
const placeholderVal = computed(() => {
  if (!props.placeholder) {
    return " ";
  }

  return props.placeholder;
});

/**
 * Auto trim on blur (if desired)
 */
const blurEvent = (event: FocusEvent) => {
  if (props.autoTrim && props.value && props.value.trim() !== props.value) {
    emit("update:value", props.value.trim());
  }

  emit("blur", event);
};

const truncatedSuffix = computed(() => {
  if (props.maxSuffixLength) {
    return truncate(props.suffix, {
      length: props.maxSuffixLength,
    });
  }

  return props.suffix;
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"

:deep(.bm-label)
  @include bm-field-label

.bm-text
  @include bm-field
</style>
